import { testIds } from '@/lib/test-ids';
import {
  Calendar,
  CheckCircle2,
  AlertCircle,
  Map,
  ClipboardList,
  RefreshCw,
  ExternalLink,
  MoreHorizontal,
  Copy,
  Share2,
  MessageSquarePlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { Section, View, SidePanelView } from './product-board-types';
import {
  normalizeBetDocFilePath,
  betDocumentReferenceId,
  getRawBetFilePath,
  getBetFileName,
  buildMcpPathSnippet,
  buildDelegatePrompt,
  dashboardBetSizeLabel,
  dashboardBetSizeBadgeClass,
} from './product-board.utils';
import { ProductBoardMarkdownPreview } from './product-board.components';

export type ProductBoardBlockedRow = { id: string; item: string; blockedOn: string; owner: string };

export type ProductBoardDashboardProps = {
  sections: Section[];
  blockedItems: ProductBoardBlockedRow[];
  lastBoardUpdate: string;
  roadmapDataReady: boolean;
  roadmapLoading: boolean;
  documentFilePath?: string;
  delegateMcpOrganizationId: string | null;
  setCurrentView: (view: View) => void;
  loadRoadmapData: () => void;
  setSidePanelBetId: (id: string) => void;
  setSidePanelView: (view: SidePanelView) => void;
};

export function ProductBoardDashboard({
  sections,
  blockedItems,
  lastBoardUpdate,
  roadmapDataReady,
  roadmapLoading,
  documentFilePath,
  delegateMcpOrganizationId,
  setCurrentView,
  loadRoadmapData,
  setSidePanelBetId,
  setSidePanelView,
}: ProductBoardDashboardProps) {
  const nowSection = sections.find((s) => s.id === 'now');
  const nowItems = nowSection?.items ?? [];
  const doneSection = sections.find((s) => s.id === 'done');
  const doneItems = doneSection?.items ?? [];
  const nextSection = sections.find((s) => s.id === 'next');
  const nextItems = nextSection?.items ?? [];

  const avgProgress =
    nowItems.length > 0
      ? Math.round(nowItems.reduce((sum, i) => sum + (i.progress ?? 0), 0) / nowItems.length)
      : 0;
  const completedNow = nowItems.filter((i) => (i.progress ?? 0) >= 100).length;
  const activeBlockers = blockedItems.filter((b) => b.item?.trim());

  return (
    <div className="space-y-6">
      {!roadmapDataReady && (
        <p className="text-xs text-blue-500 animate-pulse">Loading roadmap data...</p>
      )}

      <div className="border border-border rounded-lg p-6 bg-card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-semibold">Active Cycle</h2>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Last board update: {lastBoardUpdate}
              </span>
              <span className="text-muted-foreground/50" aria-hidden>-</span>
              <span>
                {nowItems.length} active bet{nowItems.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div
            className={`px-3 py-1 text-xs rounded font-medium border ${
              nowItems.length === 0
                ? 'bg-gray-500/10 text-gray-500 border-gray-500/20'
                : completedNow === nowItems.length
                  ? 'bg-green-500/10 text-green-500 border-green-500/20'
                  : 'bg-blue-500/10 text-blue-500 border-blue-500/20'
            }`}
          >
            {nowItems.length === 0
              ? 'No active work'
              : completedNow === nowItems.length
                ? 'Cycle complete'
                : 'In Progress'}
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm text-muted-foreground">{avgProgress}%</span>
          </div>
          <Progress value={avgProgress} className="h-2" />
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="border border-border rounded-lg p-4 bg-background">
            <div className="text-2xl font-bold mb-1">{nowItems.length}</div>
            <div className="text-xs text-muted-foreground">Active NOW</div>
            <div className="text-xs text-green-500 mt-1">{completedNow} complete</div>
          </div>
          <div className="border border-border rounded-lg p-4 bg-background">
            <div className="text-2xl font-bold mb-1">{nextItems.length}</div>
            <div className="text-xs text-muted-foreground">NEXT (queued)</div>
            <div className="text-xs text-muted-foreground mt-1">Shaped &amp; ready</div>
          </div>
          <div className="border border-border rounded-lg p-4 bg-background">
            <div className="text-2xl font-bold mb-1">{doneItems.length}</div>
            <div className="text-xs text-muted-foreground">DONE</div>
            <div className="text-xs text-muted-foreground mt-1">Completed bets</div>
          </div>
        </div>

        <div className="space-y-3">
          {activeBlockers.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-green-500">
              <CheckCircle2 className="w-4 h-4" />
              <span>No blockers</span>
            </div>
          ) : (
            <div className="p-3 border border-red-500/20 rounded bg-red-500/5">
              <div className="flex items-center gap-2 text-sm text-red-500 font-medium mb-2">
                <AlertCircle className="w-4 h-4" />
                <span>
                  {activeBlockers.length} Blocker{activeBlockers.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="space-y-2">
                {activeBlockers.map((b) => (
                  <div key={b.id} className="text-xs text-muted-foreground flex items-start gap-2 min-w-0">
                    <span className="shrink-0 mt-0.5">�</span>
                    <div className="min-w-0 space-y-1 flex-1">
                      <ProductBoardMarkdownPreview
                        content={b.item}
                        className="!mb-0 prose-sm max-w-none dark:prose-invert"
                        compact
                        linkBasePath={documentFilePath}
                      />
                      <div className="text-amber-600 dark:text-amber-400/90">
                        <ProductBoardMarkdownPreview
                          content={b.blockedOn}
                          className="!mb-0 prose-sm max-w-none dark:prose-invert"
                          compact
                          linkBasePath={documentFilePath}
                        />
                      </div>
                      {b.owner ? (
                        <span className="text-[10px] text-muted-foreground">Owner: {b.owner}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border border-border rounded-lg p-6 bg-card">
        <h3 className="font-semibold mb-3">Quick Actions</h3>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentView('roadmap')}
            className="hover:bg-primary/10"
          >
            <Map className="w-4 h-4 mr-2" />
            View Roadmap
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentView('status-board')}
            className="hover:bg-primary/10"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Status Board
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={roadmapLoading}
            onClick={() => {
              loadRoadmapData();
              toast.info('Refreshing�');
            }}
            className="hover:bg-primary/10"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${roadmapLoading ? 'animate-spin' : ''}`} />
            {roadmapLoading ? 'Loading�' : 'Refresh Data'}
          </Button>
        </div>
      </div>

      {nowItems.length > 0 && (
        <div className="border border-border rounded-lg p-6 bg-card">
          <h3 className="font-semibold mb-4">Active Bets This Cycle</h3>
          <div className="space-y-3">
            {nowItems.map((item) => {
              const progressValue = item.progress ?? 0;
              const progressColor =
                progressValue >= 80
                  ? 'bg-green-500'
                  : progressValue >= 40
                    ? 'bg-blue-500'
                    : progressValue > 0
                      ? 'bg-yellow-500'
                      : 'bg-muted-foreground/30';
              const sizeLabel = dashboardBetSizeLabel(item);

              return (
                <div
                  key={item.id}
                  data-testid={testIds.productBoard.dashboardBetCard(item.id)}
                  className="group border border-border rounded-lg p-4 hover:border-primary hover:shadow-md transition-all cursor-pointer relative"
                  onClick={() => {
                    setSidePanelBetId(item.id);
                    setSidePanelView('bet-preview');
                  }}
                  title="Click to view bet details"
                >
                  <div className="absolute top-2 right-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="absolute top-2 right-2 z-10">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                          onClick={(e) => e.stopPropagation()}
                          title="Copy path, MCP lookup, delegate�"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {normalizeBetDocFilePath(item.path) && (
                          <>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const name = getBetFileName(item.path);
                                if (name) {
                                  navigator.clipboard.writeText(name);
                                  toast.success('Filename copied', { description: name });
                                }
                              }}
                              className="gap-2"
                            >
                              <Copy className="w-4 h-4" />
                              Copy filename
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const p = getRawBetFilePath(item.path);
                                if (p) {
                                  navigator.clipboard.writeText(p);
                                  toast.success('Path copied', { description: p });
                                }
                              }}
                              className="gap-2"
                            >
                              <Copy className="w-4 h-4" />
                              Copy path
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const s = buildMcpPathSnippet(item.path, delegateMcpOrganizationId);
                                if (s) {
                                  navigator.clipboard.writeText(s);
                                  toast.success('MCP lookup copied', { description: s });
                                }
                              }}
                              className="gap-2"
                            >
                              <Copy className="w-4 h-4" />
                              Copy MCP lookup
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(buildDelegatePrompt(item, delegateMcpOrganizationId));
                                toast.success('Delegate prompt copied');
                              }}
                              className="gap-2"
                            >
                              <Share2 className="w-4 h-4" />
                              Copy delegate prompt
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const docPath = normalizeBetDocFilePath(item.path)!;
                                window.dispatchEvent(
                                  new CustomEvent('add-to-chat', {
                                    detail: {
                                      type: 'document',
                                      id: betDocumentReferenceId(item),
                                      title: item.title,
                                      name: item.title,
                                      documentType: 'bet',
                                      path: docPath,
                                    },
                                  })
                                );
                                toast.success('Added to chat', { description: item.title });
                              }}
                              className="gap-2"
                            >
                              <MessageSquarePlus className="w-4 h-4" />
                              Add to chat
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-start gap-2 mb-2 pr-8">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-medium text-sm">{item.title}</h4>
                        <span className={`px-2 py-0.5 text-xs rounded border ${dashboardBetSizeBadgeClass(sizeLabel)}`}>
                          {sizeLabel}
                        </span>
                        {progressValue >= 100 && (
                          <span className="px-2 py-0.5 text-xs rounded bg-green-500/10 text-green-500 border border-green-500/20">
                            Complete
                          </span>
                        )}
                      </div>
                      {item.appetite && (
                        <div className="text-xs text-muted-foreground">Appetite: {item.appetite}</div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{progressValue}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${progressColor}`}
                        style={{ width: `${progressValue}%` }}
                      />
                    </div>
                    {item.mustHaveTotal !== undefined && (
                      <div className="text-xs text-muted-foreground">
                        Must-haves: {item.mustHaveDone ?? 0}/{item.mustHaveTotal}
                      </div>
                    )}
                    {item.nextSteps && (
                      <div className="text-xs text-muted-foreground mt-1 truncate" title={item.nextSteps}>
                        Next: {item.nextSteps}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {nowItems.length === 0 && !roadmapLoading && roadmapDataReady && (
        <div className="border border-dashed border-border rounded-lg p-8 text-center">
          <p className="text-muted-foreground text-sm mb-3">
            No active bets in NOW. Add items via the Roadmap view.
          </p>
          <Button variant="outline" size="sm" onClick={() => setCurrentView('roadmap')}>
            <Map className="w-4 h-4 mr-2" />
            Open Roadmap
          </Button>
        </div>
      )}
    </div>
  );
}
